import { Component, Input, OnInit, ViewChild } from '@angular/core'
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms'
import { guestToSignerFormData, SignerOptionItem } from '../signer-input-base.component'
import * as lodash from 'lodash'
import { Observable } from 'rxjs'
import { map, startWith } from 'rxjs/operators'
import {
  defaultSignerOrderValue,
  SigningOrderEditorComponent,
} from '../../../template-drawer/components/signing-order-editor/signing-order-editor.component'
import { duplicatedRoleValidator } from '../../../template-drawer/components/role-item-form/role-item-form.component'
import { Destroyable, emailValidator, userFullname } from '@tokeet-frontend/tv3-platform'
import { ContractSignerType, TemplateTypes } from '@tokeet-frontend/signature'
import { AuthService } from '@tv3/services/auth.service'
import { Guest } from '@tv3/store/guest/guest.model'

export interface SignerItemFormDataWithoutRole extends SignerOptionItem {
  save?: boolean
}

export function createSignersForm(formBuilder: FormBuilder) {
  return formBuilder.array([])
}

@Component({
  selector: 'app-signers-without-role',
  templateUrl: './signers-without-role.component.html',
  styleUrls: ['./signers-without-role.component.scss'],
})
export class SignersWithoutRoleComponent extends Destroyable implements OnInit {
  @Input() form: FormArray
  @Input() isSignerCustomizable: boolean
  @Input() templateType: TemplateTypes
  @Input() defaultSigner: Guest | null = null

  @ViewChild('signingOrder') signingOrder: SigningOrderEditorComponent

  templateTypes = TemplateTypes

  rolesLimit = 5
  isMeAsSigner$: Observable<boolean>

  constructor(private fb: FormBuilder, private auth: AuthService) {
    super()
  }

  ngOnInit(): void {
    if (!this.form.length) {
      this.onAddSigner()
    }
    if (this.defaultSigner && this.form.length) {
      const signerData = guestToSignerFormData(this.defaultSigner)
      this.form.at(0).patchValue({
        ...signerData,
        data: this.defaultSigner,
      })
    }
    const currentUser = this.auth.currentUser
    this.isMeAsSigner$ = this.form.valueChanges.pipe(
      startWith(this.form.getRawValue()),
      map((signers: SignerItemFormDataWithoutRole[]) => !!lodash.find(signers, (s) => s.id === currentUser.id))
    )
  }

  trackByIndex(index: any) {
    return index
  }

  private newSignerForm(order?: number) {
    return this.fb.group({
      name: ['', [Validators.required, duplicatedRoleValidator(this.form, 'name')]],
      email: ['', [Validators.required, Validators.email, emailValidator]],
      type: [],
      id: [],
      role: [],
      order: [order || defaultSignerOrderValue],
      save: [false],
      id_photo_required: [false],
      selfie_photo_required: [false],
      data: [],
    })
  }

  private getCurrentUserAsSignerData() {
    const user = this.auth.currentUser
    return {
      name: userFullname(user),
      email: user.primaryEmail,
      type: ContractSignerType.Staff,
      id: user.id,
      role: userFullname(user),
      id_photo_required: false,
      selfie_photo_required: false,
      save: false,
      data: { ...user },
    }
  }

  onAddSigner() {
    const signerForm = this.newSignerForm(this.signingOrder?.newOrderValue)
    // always save this contact
    if (!this.isSignerCustomizable) {
      signerForm.get('save').setValue(true)
      signerForm.get('save').disable()
    }

    this.form.push(signerForm)
  }

  addMeAsOnlySigner() {
    this.form.clear()
    const signerForm = this.newSignerForm()
    signerForm.patchValue(this.getCurrentUserAsSignerData())
    this.form.push(signerForm)
  }

  onAddMe() {
    const signerData = this.getCurrentUserAsSignerData()

    let signerForm: FormGroup
    let lastSignerForm: FormGroup
    let lastSignerName: string
    let lastSignerEmail: string
    if (this.form.length) {
      lastSignerForm = this.form.at(this.form.length - 1) as FormGroup
      lastSignerName = lastSignerForm.get('name').value
      lastSignerEmail = lastSignerForm.get('email').value
    }

    const order = this.signingOrder?.newOrderValue
    signerForm =
      lastSignerName || lastSignerEmail ? this.newSignerForm(order) : lastSignerForm || this.newSignerForm(order)

    signerForm.patchValue(signerData)
    if (signerForm !== lastSignerForm) {
      this.form.push(signerForm)
    }
  }

  onRemoveSigner(index: number) {
    this.form.removeAt(index)
    this.signingOrder?.refreshOrder()
  }

  onSignerSelect(signerForm: FormGroup, signer: SignerOptionItem) {
    signerForm.patchValue({
      name: signer.name,
      email: signer.email,
      type: signer.type,
      id: signer.id,
      role: signer.role,
      data: signer.data,
    })
  }
}
